import React, { Fragment, useEffect, useReducer, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { useDebouncedCallback } from "use-debounce";

import axios from "axios";
import ai, { redirectToLogin } from "../../helpers/axios";
import usePolling from "../../helpers/usePolling";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Search from "../search";
import Loader from "../loader";
import { clientPaymentsColumns, operatorPaymentsColumns } from "./payments-list-columns";
import reducer from "./reducer";

const PaymentsContent = ({ type }) => {
  const initialState = {
    loading: true,
    data: [],
    totalLength: 0,
    query: "",
    countPerPage: 10,
    currentPage: 1,
    sortBy: "paymentDate",
    sortDirection: "desc",
  };
  const columns = type === "clientPaymentHistory" ? clientPaymentsColumns : operatorPaymentsColumns;

  const { getAccessTokenSilently } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);

  const getData = async (query, loadSilently) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let loadingStatus = !loadSilently;

    dispatch({ type: "dataBeganDownloading", payload: { loadingStatus } });

    try {
      const token = await getAccessTokenSilently().catch(() => {
        redirectToLogin();
      });

      if (!token) return;

      const { data } = await ai.auth(token).get(`/api/dashboard/payments/${type}`, {
        signal,
        params: {
          query: query.trim(),
          page: state.currentPage,
          countPerPage: state.countPerPage,
          sortBy: state.sortBy,
          sortDirection: state.sortDirection,
        },
      });

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
        },
      });
    } catch (error) {
      if (axios.isCancel(error)) return;
      console.error(error);
    }
  };

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const debouncedGetData = useDebouncedCallback(
    (query) => {
      getData(query);
    },
    500,
  );

  const handleSearch = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });

    debouncedGetData(query);
  };

  const handleSort = (column, sortDirection) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  useEffect(() => {
    getData(state.query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentPage, state.countPerPage, state.sortBy, state.sortDirection]);

  usePolling(
    () => getData(state.query, true),
    60 * 1000,
    [state.query, state.currentPage, state.countPerPage, state.sortBy, state.sortDirection],
  );

  return (
    <Fragment>
      <PageHeaderRowSearch
        h1={`Payments - ${type === "clientPaymentHistory" ? "Clients" : "Operators"}`}
        rightColumn={
          <div
            id={`payments-${type}-actions-row`}
            className="header-actions-row base-header-actions-row has-delete-btn opi-header"
          >
            <Search
              placeholder="Search"
              onSearch={handleSearch}
              query={state.query}
              className={`payments-${type}-search`}
            />
          </div>
        }
      />

      <div className={`server-pagination-table-container trips-list-table-container payments-${type}-table`}>
        <StickyHeaderDataTable
          columns={columns}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[5, 10]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.loading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
        />
      </div>
    </Fragment>
  );
};

export default PaymentsContent;
